import { useCallback, useEffect, useRef, useState } from 'react';

import {
  anAnswerIsArriving,
  stopTheArrivingAnswer,
} from 'utils/aiAnswerInProgress';

/** D30/scene 10: ten steps back, no more. */
export const UNDO_LIMIT = 10;
/**
 * Typing up to a pause is ONE step (scene 10). Below this gap two changes are
 * treated as the same stroke of work.
 */
export const COALESCE_MS = 700;

interface UseUndoRedoArgs<T> {
  /** The screen's current state. */
  value: T | undefined;
  /** Puts an earlier state back on screen. */
  apply: (value: T) => void;
  enabled?: boolean;
  /**
   * WHAT is being edited. Picking another campaign does not rebuild the screen,
   * so without this the steps belong to the campaign the person has left and
   * one press of Undo puts ITS words on the new one.
   */
  identity?: string | number | null;
  /**
   * When are two states the same step? By default when their contents match.
   * The ad builder says it itself: its ads carry things the screen and the
   * server change on their own (a picture of the drawing, a number), which
   * must travel with a step but must not make one.
   */
  same?: (a: T, b: T) => boolean;
  /**
   * Does Ctrl/Cmd+Z belong to THIS set of steps right now? For a screen that
   * shows several forms, each with steps of its own (the brands): without
   * it every form took a step back on one key press, the collapsed ones
   * unseen (review of the brand settings).
   */
  keyboardWhen?: () => boolean;
}

interface UseUndoRedoResult {
  canUndo: boolean;
  canRedo: boolean;
  undo: () => void;
  redo: () => void;
}

/**
 * Two states are the same step when their CONTENTS match. Object identity is not
 * enough: the screen hands us a fresh object on every render, and a state that
 * merely echoes back would otherwise be recorded as a step of its own, so the
 * first press of Undo would appear to do nothing.
 */
const sameState = (a: unknown, b: unknown): boolean =>
  JSON.stringify(a) === JSON.stringify(b);

const isTypingTarget = (target: EventTarget | null): boolean => {
  const el = target as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName?.toLowerCase();
  return tag === 'input' || tag === 'textarea' || el.isContentEditable === true;
};

/**
 * Does Ctrl/Cmd+Z pressed HERE step the screen back?
 *
 * Yes outside any field. Inside a field ONLY where the screen has said so with
 * `data-page-undo`, because the screen's steps know a field's words only if
 * that field reports them while they are typed:
 *
 *   data-page-undo            the editable text inside this element
 *   data-page-undo="inputs"   plain input boxes inside it as well
 *   data-native-undo          not here after all (wins over the two above)
 *
 * Everything else keeps the browser's own undo. Review round 4 (3 Oct 2026)
 * found what happens otherwise: the page title, the bullets and the button
 * text report only when the caret leaves them, so the page undo took back
 * something ELSE on the screen and left the typed words standing.
 */
export const takesPageUndo = (target: EventTarget | null): boolean => {
  if (!isTypingTarget(target)) return true;
  const el = target as HTMLElement;
  if (el.closest?.('[data-native-undo]')) return false;

  const zone = el.closest?.('[data-page-undo]');
  if (!zone) return false;

  return (
    el.isContentEditable === true ||
    zone.getAttribute('data-page-undo') === 'inputs'
  );
};

/**
 * Is the caret in a field the page undo covers? The arrows leave the caret
 * there. In any OTHER field they must take it out as a button normally does,
 * because leaving is what makes such a field report its words.
 */
export const caretIsInPageUndoField = (target: EventTarget | null): boolean =>
  isTypingTarget(target) && takesPageUndo(target);

const onAMac = (): boolean =>
  typeof navigator !== 'undefined' &&
  /Mac|iPhone|iPad/.test(navigator.platform);

/**
 * Is this a field whose words the steps know, whoever handles Ctrl/Cmd+Z in it
 * right now? `data-native-undo` decides who UNDOES; it must not decide what
 * counts as TYPING. The salary card marks itself that way while a box reads
 * "45.", and when the keys typed meanwhile stopped counting, the rest of the
 * number looked like it came after a pause: the hidden "no salary" step of
 * review round 4 (A7) was back (review round 6, E1).
 */
export const isFieldThePageKnows = (target: EventTarget | null): boolean => {
  if (!isTypingTarget(target)) return false;
  const el = target as HTMLElement;
  const zone = el.closest?.('[data-page-undo]');
  if (!zone) return false;

  return (
    el.isContentEditable === true ||
    zone.getAttribute('data-page-undo') === 'inputs'
  );
};

/** Keys that put or remove a character: what "typing" is, for the pause. */
const isTypingKey = (event: KeyboardEvent): boolean =>
  !event.metaKey &&
  !event.ctrlKey &&
  !event.altKey &&
  (event.key.length === 1 ||
    event.key === 'Backspace' ||
    event.key === 'Delete');

/**
 * Undo and Redo for one screen, for one visit (scene 10).
 *
 * Everything lives in memory on purpose: leaving the screen or reloading the
 * page empties the steps (done-point 19, test T9 and T14). It never reaches a
 * colleague's change, because it only ever knows states this screen passed
 * through.
 */
export const useUndoRedo = <T>({
  value,
  apply,
  enabled = true,
  identity = null,
  same,
  keyboardWhen,
}: UseUndoRedoArgs<T>): UseUndoRedoResult => {
  const keyboardWhenRef = useRef(keyboardWhen);
  keyboardWhenRef.current = keyboardWhen;
  const sameRef = useRef(same);
  sameRef.current = same;
  const past = useRef<T[]>([]);
  const future = useRef<T[]>([]);
  const present = useRef<T | undefined>(undefined);
  /** When the value last changed at all, which is what a pause is measured from. */
  const lastChangeAt = useRef<number>(0);
  /**
   * The last two typing keystrokes. A field can be typed in without the screen's
   * state moving ("45." and "45.0" are both "no salary"), and measuring the
   * pause from the last CHANGE alone then saw a pause in the middle of a number
   * and made "no salary" a step of its own (review round 4, A7).
   */
  const lastKeyAt = useRef<number>(0);
  const prevKeyAt = useRef<number>(0);
  /**
   * A pause that fell BEFORE typing which has not reached the screen's state
   * yet. The page title reports only when the caret leaves it: without this,
   * the typing itself made the change that follows look like part of the step
   * before the pause, and one Undo took back both.
   */
  const pauseSeen = useRef(false);
  /** True while WE are putting a state back, so it is not recorded as new work. */
  const applying = useRef(false);

  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const refresh = () => {
    setCanUndo(past.current.length > 0);
    setCanRedo(future.current.length > 0);
  };

  // A different thing is being edited: the steps of the old one are not steps.
  const identityRef = useRef(identity);
  useEffect(() => {
    if (identityRef.current === identity) return;
    identityRef.current = identity;

    past.current = [];
    future.current = [];
    present.current = undefined;
    lastChangeAt.current = 0;
    applying.current = false;
    lastKeyAt.current = 0;
    prevKeyAt.current = 0;
    pauseSeen.current = false;
    setCanUndo(false);
    setCanRedo(false);
  }, [identity]);

  useEffect(() => {
    if (!enabled || value === undefined) return;

    if (present.current === undefined) {
      present.current = value;
      return;
    }

    if (
      sameRef.current
        ? sameRef.current(value, present.current)
        : sameState(value, present.current)
    ) {
      return;
    }

    if (applying.current) {
      applying.current = false;
      present.current = value;
      refresh();
      return;
    }

    /*
     * A step is made by a PAUSE, not by the clock running on.
     *
     * The gap is measured against the previous CHANGE, and the clock is
     * re-stamped on every change. Measuring against the last recorded step
     * instead meant that continuous typing produced a new step every 0.7
     * seconds: one sentence became six or seven steps (found by hand,
     * 22 Sep 2026).
     */
    const now = Date.now();
    // Was this change made by the keystroke of a moment ago? Then the typing
    // before it is the keystroke BEFORE that one.
    const typedJustNow = now - lastKeyAt.current < 150;
    const lastTypingAt = typedJustNow ? prevKeyAt.current : lastKeyAt.current;
    const lastActivityAt = Math.max(lastChangeAt.current, lastTypingAt);
    // lastChangeAt is 0 right after an undo or redo: whatever comes next is a
    // new step, however fast it comes.
    const pausedSinceLastChange =
      lastChangeAt.current === 0 ||
      pauseSeen.current ||
      now - lastActivityAt >= COALESCE_MS;
    pauseSeen.current = false;

    if (pausedSinceLastChange) {
      past.current = [...past.current, present.current].slice(-UNDO_LIMIT);
    }
    lastChangeAt.current = now;

    // A new change makes the forward steps meaningless (scene 10).
    future.current = [];
    present.current = value;
    refresh();
  }, [value, enabled]);

  const undo = useCallback(() => {
    const previous = past.current[past.current.length - 1];
    if (previous === undefined || present.current === undefined) return;

    past.current = past.current.slice(0, -1);
    future.current = [present.current, ...future.current].slice(0, UNDO_LIMIT);
    applying.current = true;
    lastChangeAt.current = 0;
    apply(previous);
    refresh();
  }, [apply]);

  const redo = useCallback(() => {
    const next = future.current[0];
    if (next === undefined || present.current === undefined) return;

    future.current = future.current.slice(1);
    past.current = [...past.current, present.current].slice(-UNDO_LIMIT);
    applying.current = true;
    lastChangeAt.current = 0;
    apply(next);
    refresh();
  }, [apply]);

  /*
   * The keyboard does what the two buttons do, inside a text field too.
   *
   * Decided by Steven on 3 Oct 2026, replacing done-point 13 of the description:
   * left to the browser, Ctrl/Cmd+Z inside a field took a phrase back in
   * letter-sized pieces ("t", then "Tes"), four presses for one phrase, while
   * the button beside it took the whole phrase in one.
   *
   * Listened for on the way DOWN (capture) and stopped there, so neither the
   * browser nor the body-text editor runs an undo of its own underneath ours.
   */
  useEffect(() => {
    if (!enabled) return;

    const onKeyDown = (event: KeyboardEvent) => {
      /*
       * Only typing in a field whose words the steps know. Keys in a field
       * that reports late (the page title) made its report look like part of
       * the step before, and one Undo took back both (review round 5, C6).
       */
      if (isTypingKey(event) && isFieldThePageKnows(event.target)) {
        const now = Date.now();
        const quietSince = Math.max(lastChangeAt.current, lastKeyAt.current);
        if (now - quietSince >= COALESCE_MS) pauseSeen.current = true;
        prevKeyAt.current = lastKeyAt.current;
        lastKeyAt.current = now;
        return;
      }

      if (!(event.metaKey || event.ctrlKey)) return;
      if (event.altKey) return;
      const key = event.key.toLowerCase();
      // Ctrl+Y is Redo on Windows. On a Mac Cmd+Y is the browser's history and
      // Ctrl+Y is the system's own "paste back what was cut": both left alone.
      const isRedoY =
        key === 'y' && event.ctrlKey && !event.metaKey && !onAMac();
      if (key !== 'z' && !isRedoY) return;
      if (!takesPageUndo(event.target)) return;
      if (keyboardWhenRef.current && !keyboardWhenRef.current()) return;

      event.preventDefault();
      event.stopPropagation();
      // An AI answer is arriving: this press stops it and does nothing else.
      if (anAnswerIsArriving()) {
        stopTheArrivingAnswer();
        return;
      }
      if (event.shiftKey || isRedoY) redo();
      else undo();
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [enabled, undo, redo]);

  return { canUndo, canRedo, undo, redo };
};
